import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Search, Trash2 } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AdminH } from "./AdminLayout";

export const CUST_FIELDS = [["name", "Name / Company name"], ["vat_number", "VAT number"], ["id_card", "Identity Card Number"], ["address", "Address"], ["city", "City"], ["zip", "ZIP code"], ["country", "Country"], ["email", "Email"], ["phone", "Phone"]];

const EditCustomer = ({ c, onClose, onSaved }) => {
  const [f, setF] = useState(Object.fromEntries(CUST_FIELDS.map(([k]) => [k, c[k] || ""])));
  const save = async () => {
    try { await api.put(`/admin/customers/${c.id}`, { data: f }); toast.success("Customer saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-3xl" data-testid="customer-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit customer</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">{CUST_FIELDS.map(([k, l]) => (
          <label key={k} className={`text-xs font-semibold text-slate-600 ${k === "name" || k === "address" ? "sm:col-span-2" : ""}`}>{l}
            <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`customer-edit-${k.replace("_", "-")}`} /></label>))}</div>
        <div className="flex justify-end gap-3"><button onClick={onClose} className="text-sm font-semibold text-slate-500" data-testid="customer-edit-cancel">Cancel</button>
          <button onClick={save} disabled={!f.name.trim()} className="btn-buoy !py-2 disabled:opacity-40" data-testid="customer-edit-save">Save</button></div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminCustomers() {
  const [fc, setFc] = useState("");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const { data: companies } = useFetch("/admin/documents/companies");
  const { data, reload } = useFetch(`/admin/customers?company_id=${fc}`);
  const s = q.trim().toLowerCase();
  const rows = (data || []).filter((c) => !s || CUST_FIELDS.some(([k]) => (c[k] || "").toLowerCase().includes(s)));
  const del = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}? Issued documents are not changed.`)) return;
    try { await api.delete(`/admin/customers/${c.id}`); toast.success("Customer deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-customers">
      <AdminH title="Customers" sub="Recipients of manual proforma and invoices, separate for each organizing company. Saved automatically when you generate a manual document." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {(companies || []).length > 1 && <select value={fc} onChange={(e) => setFc(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm" data-testid="customers-company-filter">
          <option value="">All companies</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
        <div className="relative min-w-[260px] flex-1 sm:max-w-md">
          <Search size={16} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, VAT, city, email…" className="pl-9" data-testid="customers-search" />
        </div>
        {data && <span className="text-xs text-slate-500" data-testid="customers-count">{rows.length} of {data.length}</span>}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Name", "VAT / ID card", "Address", "Contacts", "Company", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 font-semibold text-[#0A2540]">{c.name}</td>
                <td className="px-4 py-3 text-xs">{c.vat_number || "–"}{c.id_card && <span className="block text-slate-500">ID {c.id_card}</span>}</td>
                <td className="px-4 py-3 text-xs">{[c.address, [c.zip, c.city].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ") || "–"}</td>
                <td className="px-4 py-3 text-xs">{c.email || "–"}{c.phone && <span className="block text-slate-500">{c.phone}</span>}</td>
                <td className="px-4 py-3 text-xs">{c.company_name}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button onClick={() => setEdit(c)} className="text-slate-500 hover:text-[#0A2540]" title="Edit" data-testid={`customer-edit-${c.id}`}><Pencil size={16} /></button>
                  <button onClick={() => del(c)} className="ml-3 text-slate-400 hover:text-rose-600" title="Delete" data-testid={`customer-delete-${c.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {data && !rows.length && <tr><td colSpan={6} className="px-4 py-6 text-slate-500" data-testid="customers-empty">{data.length ? "No customers match the search." : "No customers yet: they are saved when you generate a manual proforma or invoice."}</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <EditCustomer c={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
